import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check, CheckCheck, Trash2, AlertCircle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";

export default function AdminAlerts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (user && !user.is_admin) {
      navigate("/dashboard");
      toast.error("Admin access required");
    }
  }, [user, navigate]);

  const load = async (pg = 1) => {
    try {
      const { data } = await api.get("/admin/alerts", { params: { page: pg, limit: 50, unread_only: unreadOnly } });
      setAlerts(data);
      setPage(pg);
    } catch (e) {
      toast.error(apiErr(e));
      setAlerts({ alerts: [], total: 0, unread_count: 0 });
    }
  };

  useEffect(() => {
    load(1);
  }, [unreadOnly]);

  const markRead = async (alertId) => {
    setBusy(true);
    try {
      await api.post(`/admin/alerts/${alertId}/mark-read`);
      setAlerts((prev) => ({
        ...prev,
        alerts: prev.alerts.map((a) => a.id === alertId ? { ...a, read: true } : a),
        unread_count: Math.max(0, prev.unread_count - 1)
      }));
      toast.success("Marked as read");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/alerts/mark-all-read");
      toast.success(data.message);
      load(page);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteAlert = async (alertId) => {
    if (!confirm("Delete this alert?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/alerts/${alertId}`);
      setAlerts((prev) => ({
        ...prev,
        alerts: prev.alerts.filter((a) => a.id !== alertId),
        total: prev.total - 1
      }));
      toast.success("Alert deleted");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      profile_picture: "text-blue-400 border-blue-900/40 bg-blue-950/20",
      moderation: "text-red-400 border-red-900/40 bg-red-950/20",
      system: "text-amber-400 border-amber-900/40 bg-amber-950/20",
      new_profile_picture: "text-blue-400 border-blue-900/40 bg-blue-950/20",
    };
    return colors[category] || "text-zinc-400 border-zinc-800 bg-zinc-900/50";
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!user?.is_admin) {
    return null;
  }

  if (!alerts) {
    return <Loader />;
  }

  return (
    <div className="space-y-8">
      <PageHeader
        icon={Bell}
        title="Admin Alerts"
        subtitle={`${alerts.unread_count} unread notification${alerts.unread_count !== 1 ? 's' : ''}`}
      />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
              className="form-checkbox rounded"
            />
            <span>Unread only</span>
          </label>
        </div>

        <div className="flex gap-2">
          {alerts.unread_count > 0 && (
            <button
              onClick={markAllRead}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-amber-600 hover:text-amber-300 transition-colors disabled:opacity-50"
            >
              <CheckCheck size={16} /> Mark All Read
            </button>
          )}
        </div>
      </div>

      {alerts.alerts.length === 0 ? (
        <Empty label={unreadOnly ? "No unread alerts" : "No alerts yet"}>
          <button onClick={() => navigate("/admin")} className={btn()}>
            Go to Admin Panel
          </button>
        </Empty>
      ) : (
        <div className="space-y-3">
          {alerts.alerts.map((alert) => (
            <div
              key={alert.id}
              data-testid={`alert-${alert.id}`}
              className={`border rounded-sm p-4 transition-all ${
                alert.read
                  ? "border-zinc-800 bg-zinc-900/30 opacity-70"
                  : "border-amber-900/40 bg-amber-950/10"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-xs uppercase tracking-widest px-2 py-1 rounded-sm border ${getCategoryColor(
                        alert.category
                      )}`}
                    >
                      {alert.category.replace(/_/g, " ")}
                    </span>
                    {!alert.read && (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <AlertCircle size={12} /> Unread
                      </span>
                    )}
                    <span className="text-xs text-zinc-500 ml-auto">
                      {formatDate(alert.created_at)}
                    </span>
                  </div>

                  <h3 className="font-cinzel text-amber-300 text-base mb-2">
                    {alert.subject}
                  </h3>

                  <div
                    className="text-sm text-zinc-400 prose prose-invert prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: alert.body.replace(/\n/g, "<br/>"),
                    }}
                  />

                  {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400">
                        View metadata
                      </summary>
                      <pre className="text-xs text-zinc-600 mt-2 p-2 bg-zinc-950 border border-zinc-800 rounded overflow-auto">
                        {JSON.stringify(alert.metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!alert.read && (
                    <button
                      onClick={() => markRead(alert.id)}
                      disabled={busy}
                      title="Mark as read"
                      className="p-2 text-zinc-400 hover:text-emerald-400 border border-zinc-700 hover:border-emerald-600 rounded transition-colors disabled:opacity-50"
                    >
                      <Check size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => deleteAlert(alert.id)}
                    disabled={busy}
                    title="Delete alert"
                    className="p-2 text-zinc-400 hover:text-red-400 border border-zinc-700 hover:border-red-600 rounded transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {alerts.total > 50 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => load(page - 1)}
            disabled={page === 1 || busy}
            className={btn("ghost") + " disabled:opacity-50"}
          >
            Previous
          </button>
          <span className="text-sm text-zinc-400">
            Page {page} of {Math.ceil(alerts.total / 50)}
          </span>
          <button
            onClick={() => load(page + 1)}
            disabled={!alerts.has_more || busy}
            className={btn("ghost") + " disabled:opacity-50"}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
